import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { RecommendationProvider, useRecommendation, CollectedData } from '../context/RecommendationContext';
import { runDecisionEngine } from '../api/decisionEngine';
import { CurrentSystemGcFirst } from '../components/CurrentSystemGcFirst';
import { DebugBundleButton } from '../components/DebugBundleButton';
import { setMode } from '../utils/mode';
import '../styles/turbo-page.css';

// Constants for default calculations
const DRAWS_PER_BATHROOM = 8;
const DRAWS_PER_PERSON = 4;
const MAX_OCCUPANCY_FROM_BEDROOMS = 4; // Cap occupancy estimate at 4 people

// Property type icons/cards
const propertyTypes = [
  { value: 'flat', label: 'Flat', icon: '🏢' },
  { value: 'terraced', label: 'Terraced', icon: '🏘️' },
  { value: 'semi', label: 'Semi', icon: '🏡' },
  { value: 'detached', label: 'Detached', icon: '🏠' },
  { value: 'bungalow', label: 'Bungalow', icon: '🏚️' },
];

// Property age band options
const propertyAgeBandOptions = [
  { value: 'pre_1919', label: 'Pre-1919' },
  { value: '1919_1944', label: '1919-1944' },
  { value: '1945_1964', label: '1945-1964' },
  { value: '1965_1980', label: '1965-1980' },
  { value: '1981_2000', label: '1981-2000' },
  { value: '2001_2010', label: '2001-2010' },
  { value: '2011_plus', label: '2011+' },
];

// Asset paths for system images
const DATA_PATH = '/Data';

// Current system types with images
const currentSystems = [
  { value: 'combi', label: 'Combi Boiler', image: `${DATA_PATH}/Combination.png` },
  { value: 'system', label: 'System Boiler', image: `${DATA_PATH}/System-boiler.png` },
  { value: 'regular', label: 'Regular Boiler', image: `${DATA_PATH}/Open-vented-schematic.jpeg` },
  { value: 'air_source_hp', label: 'Heat Pump', image: `${DATA_PATH}/ASHP.PNG` },
  { value: 'hybrid', label: 'Hybrid Heat Pump + Boiler', image: `${DATA_PATH}/ASHP.PNG` },
];

// Hot water types
const waterSystems = [
  { value: 'on_demand', label: 'On-Demand (Combi)' },
  { value: 'unvented', label: 'Cylinder' },
  { value: 'heat_battery', label: 'Heat Battery' },
];

// Mains pressure options
const pressureOptions = [
  { value: 'high', label: 'Good' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
];

// Flow rate options
const flowOptions = [
  { value: '20', label: 'Good (20+ L/min)' },
  { value: '15', label: 'Medium (15 L/min)' },
  { value: '10', label: 'Low (10 L/min)' },
];

function TurboPageContent() {
  const navigate = useNavigate();
  const { setCollectedData, convertToSystemRecInput, setRecommendationResult } = useRecommendation();

  // Set turbo mode on mount
  useEffect(() => {
    setMode('turbo');
  }, []);

  // Form state
  const [propertyType, setPropertyType] = useState<string>('terraced');
  const [propertyAgeBand, setPropertyAgeBand] = useState<string>('1965_1980');
  const [bedrooms, setBedrooms] = useState<number>(3);
  const [bathrooms, setBathrooms] = useState<number>(1);
  const [currentSystem, setCurrentSystem] = useState<string>('combi');
  const [waterSystem, setWaterSystem] = useState<string>('on_demand');
  const [mainsPressure, setMainsPressure] = useState<string>('medium');
  const [flowRate, setFlowRate] = useState<string>('15');
  const [gcNumber, setGcNumber] = useState<string>('');
  const [sedbukBand, setSedbukBand] = useState<string>('');
  const [boilerMake, setBoilerMake] = useState<string>('');
  const [boilerModel, setBoilerModel] = useState<string>('');
  const [knownHeatLossKw, setKnownHeatLossKw] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Helper function to determine efficiency source
  const getEfficiencySource = () => {
    if (gcNumber && gcNumber.trim() !== '') {
      return 'sedbuk';
    }
    if (sedbukBand && sedbukBand.trim() !== '') {
      return 'sedbuk';
    }
    return 'unknown';
  };

  const handleRunRecommendation = async () => {
    setLoading(true);
    setError(null);

    try {
      // Build CollectedData with sensible defaults
      const data: CollectedData = {
        // Home - from form
        propertyType,
        propertyAgeBand, // User-selected age band
        bedrooms: bedrooms.toString(),
        bathrooms: bathrooms.toString(),
        
        // Home - defaults
        availableSpace: 'airing_cupboard_loft',
        outdoorSpace: 'rear_limited',

        // Occupancy - defaults based on bedrooms
        occupancyCount: Math.min(bedrooms, MAX_OCCUPANCY_FROM_BEDROOMS).toString(),
        temporaryGuests: 'occasional',
        futureDemand: 'stable',

        // Water - from form + defaults
        waterSystem,
        hotWaterDemand: bathrooms >= 3 ? 'high' : bathrooms === 2 ? 'medium' : 'low',
        usagePattern: 'mostly_showers',
        usageFrequency: 'multiple_daily',

        // Energy - from form + defaults
        boilerType: currentSystem,
        electricalCapacity: 'unknown',
        mainsPressure,
        flowRate,
        drawsPerDay: (bathrooms * DRAWS_PER_BATHROOM + Math.min(bedrooms, MAX_OCCUPANCY_FROM_BEDROOMS) * DRAWS_PER_PERSON).toString(),

        // Baseline - from form (GC-first flow)
        systemAge: '10-15',
        efficiencySource: getEfficiencySource(),
        sedbukRating: 'unknown', // Deprecated field
        sedbukBand,
        gcNumber,
        boilerMake,
        boilerModel,
        epcBand: 'unknown',
        currentFuelType: 'gas',
        sludgeRisk: 'possible',

        // Plans - balanced defaults
        lengthOfStay: 'permanent',
        lifeStage: 'established',
        budgetPriority: 'balanced',
        disruptionTolerance: 'two_three_rooms',
        urgency: 'planning',

        // System preferences - all enabled by default
        considerCombi: 'yes',
        considerSystem: 'yes',
        considerRegular: 'yes',
        considerHeatPump: 'yes',
        considerGSHP: 'yes',
        considerHybrid: 'yes',
        considerStorageCombi: 'yes',
        considerVentedCylinder: 'yes',
        considerMixergyVented: 'yes',
        considerUnventedCylinder: 'yes',
        considerMixergyUnvented: 'yes',
        considerOnDemand: 'yes',

        // LCOO - disabled by default
        enableLcoo: 'no',
      };

      // Save to context
      setCollectedData(data);

      // Convert and run recommendation
      const input = convertToSystemRecInput(data);
      const result = await runDecisionEngine(input);
      setRecommendationResult(result);

      // Persist turbo mode explicitly before navigation (belt + braces)
      setMode('turbo');
      // Navigate to story with mode in URL for reliability
      navigate('/story?mode=turbo');
    } catch (err) {
      console.error('Failed to run recommendation:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate recommendation');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToNormal = () => {
    setMode('normal');
    navigate('/story');
  };

  return (
    <div className="turbo-page">
      <div className="turbo-header">
        <button onClick={handleBackToNormal} className="turbo-back-btn">
          ← Back to Story Mode
        </button>
        <h1>Quick System Recommendation</h1>
        <p>Answer a few quick questions to get your heating system recommendation</p>
      </div>

      <div className="turbo-form">
        {/* Property Type */}
        <div className="turbo-section">
          <h2>Property Type</h2>
          <div className="turbo-tiles">
            {propertyTypes.map((type) => (
              <button
                key={type.value}
                className={`turbo-tile ${propertyType === type.value ? 'selected' : ''}`}
                onClick={() => setPropertyType(type.value)}
              >
                <span className="turbo-tile-icon">{type.icon}</span>
                <span className="turbo-tile-label">{type.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Property Age Band */}
        <div className="turbo-section">
          <h2>Property Age</h2>
          <select
            value={propertyAgeBand}
            onChange={(e) => setPropertyAgeBand(e.target.value)}
            className="form-select"
            style={{
              width: '100%',
              padding: '0.75rem',
              fontSize: '1rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontFamily: 'inherit',
            }}
          >
            {propertyAgeBandOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {/* Bedrooms */}
        <div className="turbo-section">
          <h2>Bedrooms</h2>
          <div className="turbo-stepper">
            <button
              className="turbo-stepper-btn"
              onClick={() => setBedrooms(Math.max(1, bedrooms - 1))}
              disabled={bedrooms <= 1}
            >
              −
            </button>
            <span className="turbo-stepper-value">{bedrooms}{bedrooms >= 5 ? '+' : ''}</span>
            <button
              className="turbo-stepper-btn"
              onClick={() => setBedrooms(Math.min(5, bedrooms + 1))}
              disabled={bedrooms >= 5}
            >
              +
            </button>
          </div>
        </div>

        {/* Bathrooms */}
        <div className="turbo-section">
          <h2>Bathrooms</h2>
          <div className="turbo-stepper">
            <button
              className="turbo-stepper-btn"
              onClick={() => setBathrooms(Math.max(1, bathrooms - 1))}
              disabled={bathrooms <= 1}
            >
              −
            </button>
            <span className="turbo-stepper-value">{bathrooms}{bathrooms >= 4 ? '+' : ''}</span>
            <button
              className="turbo-stepper-btn"
              onClick={() => setBathrooms(Math.min(4, bathrooms + 1))}
              disabled={bathrooms >= 4}
            >
              +
            </button>
          </div>
        </div>

        {/* Current System - GC-First Flow */}
        <div className="turbo-section">
          <h2>Current System Efficiency</h2>
          <CurrentSystemGcFirst
            gcNumber={gcNumber}
            onGcNumberChange={setGcNumber}
            sedbukBand={sedbukBand}
            onSedbukBandChange={setSedbukBand}
            boilerMake={boilerMake}
            onBoilerMakeChange={setBoilerMake}
            boilerModel={boilerModel}
            onBoilerModelChange={setBoilerModel}
          />
        </div>

        {/* Current System Type */}
        <div className="turbo-section">
          <h2>Current Heating System Type</h2>
          <div className="turbo-tiles turbo-tiles-images">
            {currentSystems.map((system) => (
              <button
                key={system.value}
                className={`turbo-tile turbo-tile-image ${currentSystem === system.value ? 'selected' : ''}`}
                onClick={() => setCurrentSystem(system.value)}
              >
                <img src={system.image} alt={system.label} className="turbo-tile-img" />
                <span className="turbo-tile-label">{system.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Hot Water System */}
        <div className="turbo-section">
          <h2>Hot Water Type</h2>
          <div className="turbo-tiles">
            {waterSystems.map((system) => (
              <button
                key={system.value}
                className={`turbo-tile ${waterSystem === system.value ? 'selected' : ''}`}
                onClick={() => setWaterSystem(system.value)}
              >
                <span className="turbo-tile-label">{system.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Optional: Mains Pressure */}
        <div className="turbo-section turbo-section-optional">
          <h2>Mains Pressure <span className="optional-badge">Optional</span></h2>
          <div className="turbo-tiles">
            {pressureOptions.map((option) => (
              <button
                key={option.value}
                className={`turbo-tile turbo-tile-small ${mainsPressure === option.value ? 'selected' : ''}`}
                onClick={() => setMainsPressure(option.value)}
              >
                <span className="turbo-tile-label">{option.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Optional: Flow Rate */}
        <div className="turbo-section turbo-section-optional">
          <h2>Flow Rate <span className="optional-badge">Optional</span></h2>
          <div className="turbo-tiles">
            {flowOptions.map((option) => (
              <button
                key={option.value}
                className={`turbo-tile turbo-tile-small ${flowRate === option.value ? 'selected' : ''}`}
                onClick={() => setFlowRate(option.value)}
              >
                <span className="turbo-tile-label">{option.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Optional: Known Heat Loss */}
        <div className="turbo-section turbo-section-optional">
          <h2>Known Heat Loss <span className="optional-badge">Optional</span></h2>
          <input
            type="number"
            value={knownHeatLossKw}
            onChange={(e) => setKnownHeatLossKw(e.target.value)}
            placeholder="Enter kW if known"
            className="form-input"
            style={{
              width: '100%',
              padding: '0.75rem',
              fontSize: '1rem',
              border: '2px solid #e2e8f0',
              borderRadius: '8px',
              fontFamily: 'inherit',
            }}
          />
          <p className="helper-text" style={{ fontSize: '0.875rem', color: '#718096', marginTop: '0.5rem' }}>
            If you know your property's heat loss from an assessment or survey, enter it here.
          </p>
        </div>

        {/* Error Display */}
        {error && (
          <div className="turbo-error">
            <strong>Error:</strong> {error}
          </div>
        )}

        {/* Actions */}
        <div className="turbo-actions">
          <button
            className="turbo-action-btn primary"
            onClick={handleRunRecommendation}
            disabled={loading}
          >
            {loading ? '⏳ Generating...' : '🚀 Get My Recommendation'}
          </button>
          
          {/* Debug Bundle Buttons - Always Visible in Turbo Mode */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', alignItems: 'center' }}>
            <p style={{ fontSize: '0.875rem', color: '#718096', margin: 0 }}>Debug Tools:</p>
            <DebugBundleButton />
          </div>
        </div>
      </div>
    </div>
  );
}

// Wrapper with RecommendationProvider
export default function TurboPage() {
  return (
    <RecommendationProvider>
      <TurboPageContent />
    </RecommendationProvider>
  );
}
